import React from "react";
import { useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { apiGetPublicTracking } from "@/lib/publicApi";
import { Loader2, AlertTriangle, Check } from "lucide-react";
import { AppCredit } from "@/components/Logo";
import PublicHeader from "@/components/PublicHeader";

const OVERALL_STYLE = {
  Selesai: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  "Perlu Perbaikan": "border-red-500/40 bg-red-500/10 text-red-300",
  "Pemeriksaan QC": "border-cyan-500/40 bg-cyan-500/10 text-cyan-300",
  "Sedang Diproses": "border-amber-500 bg-amber-500 text-slate-950",
  "Menunggu Produksi": "border-slate-600 bg-slate-800/40 text-slate-300",
  Dibatalkan: "border-red-500/40 bg-red-500/10 text-red-300",
};

// TAMPILAN-SOQ: done = orange disc with a navy tick (brand accent, 8:1);
// in progress = royal blue disc; waiting = grey. No spinning icon (the only
// animation in the app is the login page).
const StepIcon = ({ step, nomor }) => {
  if (step.done) return <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-soq-oranye text-soq-biru-tua"><Check size={16} strokeWidth={3} /></span>;
  if (step.status === "IN_PROGRESS") return <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-soq-biru text-white text-[13px] font-bold">{nomor}</span>;
  return <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-800 text-slate-500 text-[13px] font-bold">{nomor}</span>;
};

const persen = (steps) => (steps.length ? Math.round((100 * steps.filter((s) => s.done).length) / steps.length) : 0);

export default function PublicTracking() {
  const { token } = useParams();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await apiGetPublicTracking(token));
    } catch (e) {
      setError(e.response?.status === 404 ? "notfound" : "error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <PublicHeader judul="Lacak pesanan" mesin testid="tracking-header" />
      <div className="relative -mt-8 mx-auto w-full max-w-2xl px-4 pb-10">

        {loading && (
          <div data-testid="tracking-loading" className="flex items-center gap-3 text-slate-400 py-20 justify-center">
            <Loader2 className="animate-spin" size={20} /> Memuat data pelacakan…
          </div>
        )}

        {!loading && error === "notfound" && (
          <div data-testid="tracking-notfound" className="rounded-xl border border-red-500/30 bg-red-500/10 p-8 text-center">
            <AlertTriangle className="mx-auto mb-3 text-red-400" size={28} />
            <p className="text-lg font-semibold">Nomor pelacakan tidak ditemukan</p>
            <p className="text-sm text-slate-400 mt-1">Periksa kembali tautan atau QR yang Anda gunakan.</p>
          </div>
        )}

        {!loading && error === "error" && (
          <div data-testid="tracking-error" className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-center text-red-300">
            Terjadi kesalahan. <button onClick={load} className="underline">Coba lagi</button>
          </div>
        )}

        {!loading && !error && data && (
          <div data-testid="tracking-content" className="space-y-6">
            <div className="rounded-xl border border-slate-800 border-t-4 border-t-amber-500 bg-white p-6 shadow-[0_8px_24px_rgba(16,32,208,0.08)]">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[13px] font-semibold text-slate-500">{data.nomor_spk ? "No. SPK" : "No. Faktur"}</div>
                  <div data-testid="tracking-nomor-spk" className="text-xl font-mono font-bold text-soq-biru-tua">{data.nomor_spk || data.nomor_faktur}</div>
                  {data.nomor_spk && data.nomor_faktur && <div data-testid="tracking-nomor-faktur" className="text-xs font-mono text-slate-500">Faktur {data.nomor_faktur}</div>}
                  <div className="mt-2 text-sm text-slate-300">{data.customer_nama}</div>
                </div>
                <span data-testid="tracking-overall" className={`text-sm font-semibold px-3 py-1.5 rounded-lg border ${OVERALL_STYLE[data.overall_status] || OVERALL_STYLE["Menunggu Produksi"]}`}>
                  {data.overall_status}
                </span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {data.items.map((it, i) => (
                  <span key={i} className="text-xs font-semibold px-2 py-1 rounded-md border border-amber-500/40 bg-amber-50 text-amber-700">
                    {it.kategori_produk}{it.produk ? ` · ${it.produk}` : ""} × {it.quantity}
                  </span>
                ))}
              </div>
            </div>

            {data.diserahkan && (
              <div data-testid="tracking-diserahkan" className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-200">
                Pesanan sudah {data.diserahkan === "DIKIRIM" ? "dikirim" : "diambil"}. Terima kasih!
              </div>
            )}
            {data.dibatalkan && (
              <div data-testid="tracking-dibatalkan" className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-sm text-red-200">
                Pesanan ini dibatalkan. Silakan hubungi kami jika ada pertanyaan.
              </div>
            )}
            {data.workflow_instances.map((inst, idx) => (
              <div key={idx} data-testid={`tracking-instance-${inst.kategori}`} className="rounded-xl border border-slate-800 bg-white p-6">
                <div className="text-[15px] font-bold text-soq-biru-tua">
                  Alur Produksi — {inst.mesin ? `Mesin ${inst.mesin}` : inst.kategori}
                  {inst.nomor_spk && <span className="ml-2 text-xs font-mono font-normal text-slate-500">{inst.nomor_spk}</span>}
                </div>
                <div className="mt-3 mb-5 flex items-center gap-3" aria-label={`${persen(inst.steps)}% langkah selesai`}>
                  <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-800">
                    <div className="h-full rounded-full bg-soq-oranye" style={{ width: `${persen(inst.steps)}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-slate-300 tabular-nums">{inst.steps.filter((s) => s.done).length}/{inst.steps.length}</span>
                </div>
                <ol className="space-y-3">
                  {inst.steps.map((s, i) => (
                    <li key={s.urutan} data-testid={`tracking-step-${inst.kategori}-${s.urutan}`} className="flex items-center gap-3">
                      <StepIcon step={s} nomor={i + 1} />
                      <div className="flex-1">
                        <div className="text-sm text-slate-200">{s.pos_nama}</div>
                        <div className="text-xs text-slate-500">
                          {s.is_qc && s.qc_label ? s.qc_label : s.status_label}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            ))}

            <div className="rounded-xl border border-slate-800 border-t-4 border-t-amber-500 bg-white p-6 flex flex-col items-center gap-3">
              <div className="text-[13px] font-semibold text-slate-500">Bagikan / Simpan QR</div>
              <div className="bg-white p-3 rounded-lg">
                <QRCodeCanvas data-testid="tracking-qr" value={data.tracking_url} size={132} />
              </div>
              <button data-testid="tracking-refresh" onClick={load} className="min-h-[44px] rounded-lg bg-soq-oranye px-5 text-sm font-semibold text-soq-biru-tua hover:bg-amber-400">Perbarui status</button>
            </div>
          </div>
        )}
        <AppCredit className="mt-10" />
      </div>
    </div>
  );
}
